import type React from "react";

import * as PatientListV4I from "./common/sequence/PatientListV4";
import * as CommonInterface from "./common/commonV4Interface";
import * as ExamV4I from "./common/sequence/ExamV4";
import * as ExamDispatchI from "./patient/examDispatchV4";
import * as PatientOrdersV4I from "./common/sequence/PatientOrdersV4";
import * as MultidisciplinaryV4I from "./common/sequence/MultidisciplinaryV4";
import * as OrderSheetV4I from "./common/sequence/OrderSheetV4";
import * as OrderAppointmentV4I from "./common/sequence/OrderAppointmentV4";
import * as ResultSummaryV4I from "./common/sequence/ResultSummaryV4";
import * as PatientHistoryV4I from "./common/sequence/PatientHistoryV4";
import * as DocumentV4I from "./common/sequence/DocumentV4";
import * as ScreenV4I from "./common/sequence/ScreenV4";

/* ------------------------------------------------------ */

/*          MainHISV4 interface (State / Event / Data)    */
/*          รวม sequence ของ HISV4 ไว้ที่ไฟล์นี้ไฟล์เดียว  */
/*          (MainHISInterface เรียกใช้ HISV4Interface เท่านั้น) */

/* ------------------------------------------------------ */

export { default as SequencePattern } from "./common/sequence/SequencePattern";

export type Props = CommonInterface.Props;

export type State = CommonInterface.State;

export const StateInitial: State = {
  ...CommonInterface.StateInitial,
};

export type Event = CommonInterface.Event;

export type Data = CommonInterface.Data;

export const DataInitial = CommonInterface.DataInitial;

/* ------------------------------------------------------ */

/*                        TYPE HELPERS                    */

/* ------------------------------------------------------ */

export type PatientListV4State = PatientListV4I.State;
export type PatientListV4SequenceState = PatientListV4I.State["PatientListV4Sequence"];
export type PatientListV4Filter = PatientListV4I.FilterType;
export type PatientListV4Event = PatientListV4I.Event;
export type OnEvent = PatientListV4I.OnEvent;
export type RunSequence = PatientListV4I.RunSequence;
/** params ที่ card ของคิวต้องส่งให้ runSequence (helper ประกอบ event ถูกถอดออกแล้ว) */
export type PatientListRunParams = PatientListV4I.PatientListRunParams;
export type PatientListRunSequenceProp = PatientListV4I.PatientListRunSequenceProp;
export type PatientListV4Slice = PatientListV4I.PatientListV4Slice;

export const PATIENT_LIST_V4_SEQUENCE = PatientListV4I.SEQUENCE_NAME;
export const PATIENT_LIST_V4_CARD = PatientListV4I.CARD_NAME;
export const PATIENT_LIST_V4_ACTIONS = PatientListV4I.ACTIONS;
/** key ของ state.buttonLoadCheck เช่น CardPatientListV4_SEARCH */
export const PATIENT_LIST_V4_BTN_ACTS = PatientListV4I.BTN_ACTS;

export const normalizePatientListV4Slice = PatientListV4I.normalizePatientListV4Slice;

export const normalizePatientListV4Filter = PatientListV4I.NormalizeFilter;

/* ------------------------------------------------------ */

/*                ExamV4 (หน้าตรวจรักษา)                    */

/* ------------------------------------------------------ */

export type ExamV4SequenceState = ExamV4I.State["ExamV4Sequence"];
export type ExamV4ActionName = ExamV4I.ActionName;
/** state ของ django ที่ MainHIS มี (ใช้ role_types ตัดสินสิทธิ์) */
export type DjangoState = {
  user?: {
    id?: number;
    token?: string;
    role_types?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

/**
 * shape อย่างย่อของ state ที่ MainHIS (parent) ส่งเข้ามาให้ HIS V4
 * backend ส่ง field มากกว่านี้ จึงเปิด index signature ไว้กัน field ใหม่
 */
export type SelectedEmrState = {
  id?: number;
  doctor?: number;
  doctor_name?: string;
  [key: string]: unknown;
};

/** flag ของผู้ป่วย (แพ้ / โรคเรื้อรัง) ที่ PatientPanelV4 อ่าน */
export type PatientFlagState = {
  items?: unknown[];
  [key: string]: unknown;
};

export type ExamV4SliceState = ExamV4I.State["ExamV4Sequence"];
/** params ที่ card ของหน้าตรวจรักษาต้องส่งให้ runSequence */
export type ExamRunParams = ExamV4I.ExamRunParams;
export type ExamRunSequence = ExamV4I.RunSequence;

export const EXAM_V4_SEQUENCE = CommonInterface.EXAM_V4_SEQUENCE;
export const EXAM_V4_CARD = CommonInterface.EXAM_V4_CARD;
export const EXAM_ACTIONS = CommonInterface.EXAM_ACTIONS;
export const EXAM_V4_CARDS = CommonInterface.EXAM_V4_CARDS;
export const EXAM_V4_BTN_ACTS = CommonInterface.EXAM_V4_BTN_ACTS;
export const ACTION_START_EXAM = CommonInterface.ACTION_START_EXAM;

export { ExamV4I, ExamDispatchI };

export type PatientOrdersV4SequenceState = PatientOrdersV4I.State["PatientOrdersV4Sequence"];

/* ------------- MultidisciplinaryV4 (บันทึกทีมสหสาขา) ------------- */

export type MultidisciplinaryV4SequenceState =
  MultidisciplinaryV4I.State["MultidisciplinaryV4Sequence"];
export type MultidisciplinaryRunParams = MultidisciplinaryV4I.ConsultRunParams;
export type MultidisciplinaryRunSequenceProp = MultidisciplinaryV4I.ConsultRunSequenceProp;

export const CONSULT_V4_SEQUENCE = CommonInterface.CONSULT_V4_SEQUENCE;
export const CONSULT_V4_CARD = CommonInterface.CONSULT_V4_CARD;
export const CONSULT_ACTIONS = CommonInterface.CONSULT_ACTIONS;
export const CONSULT_V4_BTN_ACTS = CommonInterface.CONSULT_V4_BTN_ACTS;

export type OrderSheetV4SequenceState = OrderSheetV4I.State["OrderSheetV4Sequence"];
export type OrderAppointmentV4SequenceState =
  OrderAppointmentV4I.State["OrderAppointmentV4Sequence"];

/* ------------- ResultSummaryV4 (หน้า Result Summary) ------------- */

export type ResultSummaryV4SequenceState = CommonInterface.ResultSummaryV4SequenceState;

/* ------------- PatientHistoryV4 (หน้า Patient History Viewer 4d) ------------- */

export type PatientHistoryV4SequenceState = PatientHistoryV4I.State["PatientHistoryV4Sequence"];
export type PatientHistoryV4ActionName = PatientHistoryV4I.ActionName;
export type PatientHistoryRunParams = PatientHistoryV4I.HistoryRunParams;
export type PatientHistoryRunSequenceProp = PatientHistoryV4I.HistoryRunSequenceProp;
export type PatientHistoryV4State = PatientHistoryV4I.State;
export type PatientHistoryV4Data = PatientHistoryV4I.Data;
export type PatientHistoryV4Event = PatientHistoryV4I.Event;
export type ResultSummaryRunParams = CommonInterface.ResultSummaryRunParams;
export type ResultSummaryRunSequenceProp = CommonInterface.ResultSummaryRunSequenceProp;

export const RESULT_SUMMARY_V4_SEQUENCE = CommonInterface.RESULT_SUMMARY_V4_SEQUENCE;
export const RESULT_SUMMARY_V4_CARD = CommonInterface.RESULT_SUMMARY_V4_CARD;
export const RESULT_SUMMARY_ACTIONS = CommonInterface.RESULT_SUMMARY_ACTIONS;
export const RESULT_SUMMARY_V4_BTN_ACTS = CommonInterface.RESULT_SUMMARY_V4_BTN_ACTS;

/* ------------- DocumentV4 (หน้า Document) ------------- */

export type DocumentV4SequenceState = DocumentV4I.State["DocumentV4Sequence"];
export const DOCUMENT_V4_SEQUENCE = DocumentV4I.SEQUENCE_NAME;
export const DOCUMENT_V4_CARD = DocumentV4I.CARD_NAME;
export const DOCUMENT_ACTIONS = DocumentV4I.ACTIONS;
export const DOCUMENT_V4_BTN_ACTS = DocumentV4I.BTN_ACTS;

/* ------------- ScreenV4 (หน้าคัดกรอง / Screening) ------------- */

export type ScreenV4SequenceState = ScreenV4I.State["ScreenV4Sequence"];
export const SCREEN_V4_SEQUENCE = ScreenV4I.SEQUENCE_NAME;
export const SCREEN_V4_CARD = ScreenV4I.CARD_NAME;
export const SCREEN_ACTIONS = ScreenV4I.ACTIONS;
export const SCREEN_V4_BTN_ACTS = ScreenV4I.BTN_ACTS;

export {
  PatientListV4I,
  PatientOrdersV4I,
  MultidisciplinaryV4I,
  OrderSheetV4I,
  OrderAppointmentV4I,
  ResultSummaryV4I,
  PatientHistoryV4I,
  DocumentV4I,
  ScreenV4I,
};

/** หน้าของ V3 ที่ MainHIS ประกอบส่งลงมาวางในกรอบ V4 */
export type V3PageKey = "mePOSQueue" | "servicesQueue" | "report" | "mePOSMenu" | "patientInfo";
export type V3Pages = Partial<
  Record<V3PageKey, (arg?: string, onSelect?: (key: string) => void) => React.ReactNode>
>;
